import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Skeleton,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router-dom";
import reportAPI, {
  ComprehensiveReport,
  ReportFilter,
  ReportType,
  SalesSummaryReport,
} from "../../services/reportAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { useAuthStore } from "../../stores/authStore";
import PageHeader from "../../components/layout/PageHeader";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { sortRows } from "../../utils/sortRows";
import { tokens } from "../../theme";

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatNumber = (value: number | string) =>
  new Intl.NumberFormat("en-IN").format(Number(value) || 0);

const defaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return format(d, "yyyy-MM-dd");
};

const defaultEndDate = () => format(new Date(), "yyyy-MM-dd");

const REPORT_TYPES: { value: ReportType; label: string; description: string }[] = [
  {
    value: "sales-summary",
    label: "Sales summary",
    description: "Daily sales, payment methods & top products",
  },
  {
    value: "comprehensive",
    label: "Comprehensive",
    description: "Sales, purchases, inventory & NFC transactions",
  },
];

function isComprehensive(data: unknown): data is ComprehensiveReport {
  return Boolean(data && typeof data === "object" && "summary" in data);
}

function isSalesSummary(data: unknown): data is SalesSummaryReport {
  return Boolean(data && typeof data === "object" && "dailySales" in data);
}

const StatTile: React.FC<{
  label: string;
  value: string;
  tone?: "primary" | "success" | "info" | "warning";
}> = ({ label, value, tone = "primary" }) => {
  const theme = useTheme();
  const color = theme.palette[tone].main;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        borderTop: `3px solid ${color}`,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const ReportsPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isSuperAdmin = role === "SUPER_ADMIN" || role === "SUPERADMIN";
  const isAdmin = role === "ADMIN";

  const [draft, setDraft] = useState<ReportFilter>({
    startDate: defaultStartDate(),
    endDate: defaultEndDate(),
    reportType: "sales-summary",
    branchId: isAdmin ? user?.branchId : undefined,
  });
  const [filters, setFilters] = useState<ReportFilter>(draft);
  const {
    sortBy: lowStockSortBy,
    sortOrder: lowStockSortOrder,
    handleSort: handleLowStockSort,
  } = useTableSort<"product" | "sku" | "available" | "minStock">("product", "asc");
  const {
    sortBy: txSortBy,
    sortOrder: txSortOrder,
    handleSort: handleTxSort,
  } = useTableSort<"createdAt" | "holder" | "type" | "branch" | "amount">("createdAt", "desc");
  const {
    sortBy: topProductSortBy,
    sortOrder: topProductSortOrder,
    handleSort: handleTopProductSort,
  } = useTableSort<"product" | "sku" | "quantity" | "revenue">("revenue", "desc");

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const {
    data: reportData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["reports", filters],
    queryFn: () => reportAPI.getReport(filters),
    retry: 1,
  });

  const applyFilters = () => {
    const next = { ...draft };
    if (isAdmin && user?.branchId) {
      next.branchId = user.branchId;
    }
    setFilters(next);
  };

  const chartTooltip = {
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: 12,
  };

  const salesChartData = useMemo(() => {
    if (!isSalesSummary(reportData)) return [];
    return (reportData.dailySales || []).map((row) => ({
      date: String(row.date).slice(5),
      amount: Number(row.totalAmount) || 0,
      count: Number(row.transactionCount) || 0,
    }));
  }, [reportData]);

  const paymentChartData = useMemo(() => {
    if (!isSalesSummary(reportData)) return [];
    return (reportData.salesByPaymentMethod || []).map((row, i) => ({
      name: row.paymentMethod || "Other",
      value: Number(row.totalAmount) || 0,
      color: [tokens.colors.primary.main, tokens.colors.secondary.main, tokens.colors.success.main, tokens.colors.warning.main][i % 4],
    }));
  }, [reportData]);

  const txTypeData = useMemo(() => {
    if (!isComprehensive(reportData)) return [];
    const s = reportData.summary;
    return [
      { name: "Credit", value: s.creditTransactionCount, color: tokens.colors.success.main },
      { name: "Debit", value: s.debitTransactionCount, color: tokens.colors.primary.main },
    ].filter((d) => d.value > 0);
  }, [reportData]);

  const sortedLowStock = useMemo(() => {
    if (!isComprehensive(reportData)) return [];
    return sortRows(
      reportData.inventory?.lowStockItems || [],
      lowStockSortBy,
      lowStockSortOrder,
      (row, field) => {
        if (field === "sku") return row.product?.sku ?? "";
        if (field === "available") return row.availableQuantity;
        if (field === "minStock") return row.minStockAlert ?? row.reorderLevel ?? 0;
        return row.product?.name ?? "";
      }
    );
  }, [reportData, lowStockSortBy, lowStockSortOrder]);

  const sortedReportTransactions = useMemo(() => {
    if (!isComprehensive(reportData)) return [];
    return sortRows(
      reportData.transactions || [],
      txSortBy,
      txSortOrder,
      (row, field) => {
        if (field === "holder") return row.cardHolder?.holderName ?? "";
        if (field === "type") return row.type;
        if (field === "branch") return row.branch?.name ?? "";
        if (field === "amount") return Number(row.amount ?? 0);
        return row.transactionDate || row.createdAt;
      }
    );
  }, [reportData, txSortBy, txSortOrder]);

  const sortedTopProducts = useMemo(() => {
    if (!isSalesSummary(reportData)) return [];
    return sortRows(
      reportData.topSellingProducts || [],
      topProductSortBy,
      topProductSortOrder,
      (row, field) => {
        const name =
          row.product?.name || (row as { "product.name"?: string })["product.name"] || "";
        const sku = row.product?.sku || (row as { "product.sku"?: string })["product.sku"] || "";
        if (field === "sku") return sku;
        if (field === "quantity") return Number(row.totalQuantity ?? 0);
        if (field === "revenue") return Number(row.totalRevenue ?? 0);
        return name;
      }
    );
  }, [reportData, topProductSortBy, topProductSortOrder]);

  const errorMessage = error
    ? error instanceof Error
      ? error.message
      : (error as unknown as { response?: { data?: { message?: string } } }).response
          ?.data?.message || "Failed to load report"
    : "";

  return (
    <Box>
      <PageHeader
        title="Reports"
        subtitle="Generate insights for your selected date range and branch"
        action={
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to="/reports/sales" variant="outlined">
              Open sales report
            </Button>
            <Button
              component={RouterLink}
              to="/reports/gst-books"
              variant="outlined"
            >
              GST & books
            </Button>
          </Stack>
        }
      />

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterListIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            Filters
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft((p) => ({ ...p, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft((p) => ({ ...p, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={draft.branchId || ""}
                  label="Branch"
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      branchId: e.target.value || undefined,
                      salesPointId: undefined,
                    }))
                  }
                >
                  <MenuItem value="">All branches</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {isAdmin && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Branch"
                value="Your branch"
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>SalesPoint</InputLabel>
                <Select
                  value={draft.salesPointId || ""}
                  label="Sales point"
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      salesPointId: e.target.value || undefined,
                    }))
                  }
                >
                  <MenuItem value="">All sales points</MenuItem>
                  {salesPoints.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Report type</InputLabel>
              <Select
                value={draft.reportType}
                label="Report type"
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    reportType: e.target.value as ReportType,
                    salesPointId: undefined,
                  }))
                }
              >
                {REPORT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={applyFilters}
              disabled={isFetching}
            >
              Generate
            </Button>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          {REPORT_TYPES.find((t) => t.value === draft.reportType)?.description}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {isLoading && (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={320} />
          </Grid>
        </Grid>
      )}

      {!isLoading && !error && reportData && isComprehensive(reportData) && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${format(new Date(filters.startDate), "dd MMM yyyy")} – ${format(new Date(filters.endDate), "dd MMM yyyy")}`}
            />
            {reportData.branch && (
              <Chip size="small" color="primary" variant="outlined" label={reportData.branch.name} />
            )}
            {reportData.salesPoint && (
              <Chip size="small" color="secondary" variant="outlined" label={reportData.salesPoint.name} />
            )}
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              sx={{ ml: "auto" }}
            >
              Refresh
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="Sales amount" value={formatCurrency(reportData.summary.totalSalesAmount)} tone="success" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="Sales count" value={formatNumber(reportData.summary.totalSales)} tone="success" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="Purchases" value={formatCurrency(reportData.summary.totalPurchaseAmount)} tone="primary" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="NFC transactions" value={formatNumber(reportData.summary.totalTransactions)} tone="info" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="Transaction volume" value={formatCurrency(reportData.summary.totalTransactionAmount)} tone="info" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="Inventory value" value={formatCurrency(reportData.summary.totalInventoryValue)} tone="warning" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile label="Low stock items" value={formatNumber(reportData.summary.lowStockItemsCount)} tone="warning" />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <StatTile
                label="Credits / Debits"
                value={`${reportData.summary.creditTransactionCount} / ${reportData.summary.debitTransactionCount}`}
                tone="primary"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    NFC transaction split
                  </Typography>
                  {txTypeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={txTypeData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {txTypeData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={chartTooltip} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                      No transactions in range
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Low stock products
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <SortableTableCell label="Product" field="product" sortBy={lowStockSortBy} sortOrder={lowStockSortOrder} onSort={handleLowStockSort} />
                          <SortableTableCell label="SKU" field="sku" sortBy={lowStockSortBy} sortOrder={lowStockSortOrder} onSort={handleLowStockSort} />
                          <SortableTableCell label="Available" field="available" sortBy={lowStockSortBy} sortOrder={lowStockSortOrder} onSort={handleLowStockSort} align="right" />
                          <SortableTableCell label="Min stock" field="minStock" sortBy={lowStockSortBy} sortOrder={lowStockSortOrder} onSort={handleLowStockSort} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedLowStock.slice(0, 8).map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>{item.product?.name || "—"}</TableCell>
                            <TableCell>{item.product?.sku || "—"}</TableCell>
                            <TableCell align="right">{item.availableQuantity}</TableCell>
                            <TableCell align="right">
                              {item.minStockAlert ?? item.reorderLevel ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(reportData.inventory?.lowStockItems?.length ?? 0) === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" py={2}>
                                No low stock items
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Recent NFC transactions
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <SortableTableCell label="Date" field="createdAt" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                          <SortableTableCell label="Holder" field="holder" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                          <SortableTableCell label="Type" field="type" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                          <SortableTableCell label="Branch" field="branch" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                          <SortableTableCell label="Amount" field="amount" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedReportTransactions.slice(0, 10).map((tx) => (
                          <TableRow key={tx.id} hover>
                            <TableCell>
                              {format(
                                new Date(tx.transactionDate || tx.createdAt),
                                "dd MMM yyyy HH:mm"
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {tx.cardHolder?.holderName || "—"}
                              </Typography>
                              {tx.description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {tx.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={tx.type}
                                size="small"
                                color={tx.type === "credit" ? "success" : "error"}
                                sx={{ textTransform: "capitalize" }}
                              />
                            </TableCell>
                            <TableCell>{tx.branch?.name || "—"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCurrency(tx.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(reportData.transactions?.length ?? 0) === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary" py={2}>
                                No transactions in selected period
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {!isLoading && !error && reportData && isSalesSummary(reportData) && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${format(new Date(filters.startDate), "dd MMM yyyy")} – ${format(new Date(filters.endDate), "dd MMM yyyy")}`}
            />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              sx={{ ml: "auto" }}
            >
              Refresh
            </Button>
          </Stack>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Sales by payment method
                  </Typography>
                  {paymentChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={paymentChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {paymentChartData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatCurrency(v)} contentStyle={chartTooltip} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" py={6} textAlign="center">
                      No sales in selected period
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Daily sales
                  </Typography>
                  {salesChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={salesChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(v: number) => formatCurrency(v)} contentStyle={chartTooltip} />
                        <Legend />
                        <Bar
                          dataKey="amount"
                          name="Amount"
                          fill={tokens.colors.primary.main}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={40}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" py={6} textAlign="center">
                      No daily sales data
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Top selling products
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <SortableTableCell label="Product" field="product" sortBy={topProductSortBy} sortOrder={topProductSortOrder} onSort={handleTopProductSort} />
                          <SortableTableCell label="SKU" field="sku" sortBy={topProductSortBy} sortOrder={topProductSortOrder} onSort={handleTopProductSort} />
                          <SortableTableCell label="Qty sold" field="quantity" sortBy={topProductSortBy} sortOrder={topProductSortOrder} onSort={handleTopProductSort} align="right" />
                          <SortableTableCell label="Revenue" field="revenue" sortBy={topProductSortBy} sortOrder={topProductSortOrder} onSort={handleTopProductSort} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedTopProducts.map((row, idx) => {
                          const name =
                            row.product?.name ||
                            (row as { "product.name"?: string })["product.name"] ||
                            "—";
                          const sku =
                            row.product?.sku ||
                            (row as { "product.sku"?: string })["product.sku"] ||
                            "—";
                          return (
                            <TableRow key={row.productId || idx} hover>
                              <TableCell>{name}</TableCell>
                              <TableCell>{sku}</TableCell>
                              <TableCell align="right">{formatNumber(row.totalQuantity)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(row.totalRevenue)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {(reportData.topSellingProducts?.length ?? 0) === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" py={2}>
                                No product sales in period
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ReportsPage;
